import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/useAuthStore';
import { isSnapfyInternal } from '../../utils/snapfyInternal';
import { generationModelsService, type GenerationModelOption } from '../../services/generationModelsService';

interface ModelPinSelectProps {
    value?: string;
    onChange: (modelVersion: string | undefined) => void;
    disabled?: boolean;
}

/**
 * Escolha do modelo de IA para esta edição — ferramenta interna (@snapfy.ai).
 *
 * O componente cuida do gate e da busca da lista, para os modais só o
 * pendurarem. Renderiza NULL quando o usuário não é da Snapfy ou quando não há
 * modelo habilitado, que é o mesmo caso: o backend responde lista vazia.
 *
 * A opção vazia é o padrão e significa "usar a cadeia configurada" — sem ela,
 * abrir o modal passaria a fixar um modelo sem ninguém ter pedido.
 */
const ModelPinSelect: React.FC<ModelPinSelectProps> = ({ value, onChange, disabled }) => {
    const user = useAuthStore(state => state.user);
    const isInternal = isSnapfyInternal(user);
    const [models, setModels] = useState<GenerationModelOption[]>([]);

    useEffect(() => {
        if (!isInternal) return;
        let cancelled = false;
        generationModelsService.list()
            .then(list => { if (!cancelled) setModels(list); })
            .catch(() => { if (!cancelled) setModels([]); });
        return () => { cancelled = true; };
    }, [isInternal]);

    if (!isInternal || models.length === 0) return null;

    return (
        <div data-testid="model-pin-select">
            <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">
                Modelo de IA <span className="text-snapfy-text-secondary/60">(interno)</span>
            </label>
            <select
                value={value ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-xl bg-snapfy-inner border border-snapfy-border text-snapfy-text-primary focus:ring-snapfy-focus disabled:opacity-50"
            >
                <option value="">Padrão (cadeia configurada)</option>
                {models.map(model => (
                    <option key={model.id} value={model.id}>{model.label}</option>
                ))}
            </select>
        </div>
    );
};

export default ModelPinSelect;
